import type { PlayEventMatch, PlayEventParticipant, PlayEventTeam, PlayEventDisplayParticipant } from '@court-sync/shared';
import {
  formatPlayEventMatchScoreLabel,
  getPlayEventMatchSideNames,
  getPlayEventMatchSideParticipants,
} from '@court-sync/shared';
import { cn } from '@/lib/utils';
import { PlayEventParticipantRow } from '@/components/play/PlayEventParticipantAvatar';
import { playEventDisplayTheme } from '@/components/play/playEventDisplayTheme';

type PlayEventParticipantLike = PlayEventDisplayParticipant;

interface PlayEventMatchupCardProps {
  match: PlayEventMatch;
  participantsById: Map<string, PlayEventDisplayParticipant>;
  teamsById: Map<string, PlayEventTeam>;
  courtName?: string | null;
  statusLabel: string;
  celebrateWinner?: boolean;
}

function MatchSideBlock({
  participants,
  align,
  isWinner,
  celebrateWinner,
}: {
  participants: PlayEventParticipantLike[];
  align: 'left' | 'right';
  isWinner: boolean;
  celebrateWinner: boolean;
}) {
  return (
    <div
      className={cn(
        align === 'right' ? 'space-y-2 text-right sm:text-right' : 'space-y-2 text-left sm:text-left',
        isWinner &&
          'rounded-lg border border-emerald-500/70 bg-emerald-50 px-2 py-2 dark:border-emerald-400/50 dark:bg-emerald-500/10',
        celebrateWinner &&
          isWinner &&
          'motion-safe:animate-pulse ring-2 ring-emerald-400/80 ring-offset-2 ring-offset-gray-50 dark:ring-offset-black/25'
      )}
    >
      {participants.map((participant) => (
        <PlayEventParticipantRow
          key={participant.id}
          participant={participant}
          align={align}
          size="md"
          nameClassName={cn('text-base sm:text-lg', isWinner && 'font-bold text-emerald-900 dark:text-emerald-100')}
        />
      ))}
    </div>
  );
}

export function PlayEventMatchupCard({
  match,
  participantsById,
  teamsById,
  courtName,
  statusLabel,
  celebrateWinner = false,
}: PlayEventMatchupCardProps) {
  const { sideA, sideB } = getPlayEventMatchSideParticipants(match, participantsById, teamsById);
  const { sideA: sideANames } = getPlayEventMatchSideNames(match, participantsById, teamsById);
  const isBye = match.result === 'bye';
  const scoreLabel = formatPlayEventMatchScoreLabel(match.score);

  return (
    <article className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-black/25">
      <header
        className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-100/80 px-4 py-2.5 sm:px-5 dark:border-white/10 dark:bg-white/[0.02]"
      >
        <span className="truncate text-xs font-semibold uppercase tracking-widest text-primary-700 dark:text-primary-400/90">
          {courtName ?? 'Court TBD'}
        </span>
        <span className={`shrink-0 text-xs capitalize ${playEventDisplayTheme.mutedFaint}`}>
          {scoreLabel ? `${scoreLabel} · ${statusLabel}` : statusLabel}
        </span>
      </header>

      {isBye ? (
        <div className="px-4 py-5 sm:px-5 sm:py-6">
          <p className="text-center text-lg font-semibold text-gray-800 dark:text-white/80">
            {sideANames[0]}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 px-4 py-5 sm:gap-4 sm:px-5 sm:py-6">
          <MatchSideBlock
            participants={sideA}
            align="right"
            isWinner={match.winnerSide === 'a'}
            celebrateWinner={celebrateWinner}
          />
          <span
            className="rounded-full bg-primary-100 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.2em] text-primary-700 dark:bg-primary-500/15 dark:text-primary-400 sm:text-xs"
            aria-hidden
          >
            vs
          </span>
          <MatchSideBlock
            participants={sideB}
            align="left"
            isWinner={match.winnerSide === 'b'}
            celebrateWinner={celebrateWinner}
          />
        </div>
      )}
    </article>
  );
}

export function PlayEventParticipantChip({
  participant,
}: {
  participant: PlayEventParticipant;
}) {
  return <PlayEventParticipantRow participant={participant} size="sm" nameClassName="text-sm" />;
}
